import {
  BuyableItemCreationType,
  GetUserBasket,
  MethodPayment,
} from "@ac-expo/api-types";
import { toDatabaseFormat } from "@ac-expo/utils";
import { BasketType, Role } from "@prisma/client";
import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { privateAxios } from "../axiosInstances";
import { basketQueryKeys } from "../query-keys";
import { ApplyDiscountCodeForm } from "../schemas";

interface ValidateBasketBody {
  basketId: string;
  displayedPrice: number;
  role?: Role;
  paymentType?: MethodPayment;
}

async function validateBasket(body: ValidateBasketBody) {
  const res = await privateAxios.post<{ id: string; reference: string }>(
    `/basket/validate`,
    body
  );
  return res.data;
}

export function useCheckoutBasketMutation() {
  return useMutation({
    mutationFn: validateBasket,
  });
}

export type AddTicket = {
  id: string;
  quantity: number;
};

export type BuyableItemWithQuantity = {
  quantity: number;
  buyableItem: BuyableItemCreationType;
};

export type AddBuyableItems = {
  items: BuyableItemWithQuantity[];
};

export async function addItems(body: AddBuyableItems, type: BasketType) {
  const { data } = await privateAxios.post<void>("/basket/items", {
    ...body,
    type,
  });
  return data;
}

export type UseAddItemsMutationOptions = UseMutationOptions<
  unknown,
  Error,
  AddBuyableItems,
  unknown
>;

export const useAddItemsMutation = (
  type: BasketType = "store",
  options?: UseAddItemsMutationOptions
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: AddBuyableItems) => addItems(body, type),
    ...options,
    onSuccess: (...args) => {
      options?.onSuccess?.(...args);
      queryClient.invalidateQueries({
        queryKey: basketQueryKeys.get(type),
      });
      queryClient.invalidateQueries({
        queryKey: basketQueryKeys.activityWarning(),
      });
    },
  });
};

export type SyncBasketDto = {
  type: BasketType;
  items: BuyableItemWithQuantity[];
};

export type SyncBasketMutationResult = {
  quantities: Record<string, number>;
  basket: GetUserBasket;
};

export async function syncBasket(body: SyncBasketDto) {
  const { data } = await privateAxios.put<SyncBasketMutationResult>(
    "/basket/sync",
    body
  );
  return data;
}

export type UseSyncBasketMutationOptions = UseMutationOptions<
  SyncBasketMutationResult,
  Error,
  SyncBasketDto,
  unknown
>;

export const useSyncBasketMutation = (
  type: BasketType = "store",
  options?: UseSyncBasketMutationOptions
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: syncBasket,
    ...options,
    onSuccess: (data, ...rest) => {
      options?.onSuccess?.(data, ...rest);
      // Seed the cache synchronously so consumers (e.g. guest checkout that
      // clears localStorage right after sync) don't flash a 0€ total while
      // waiting for the invalidation refetch.
      queryClient.setQueryData(basketQueryKeys.get(type), data.basket);
      queryClient.invalidateQueries({
        queryKey: basketQueryKeys.get(type),
      });
      queryClient.invalidateQueries({
        queryKey: basketQueryKeys.activityWarning(),
      });
    },
  });
};

export type UpdateEventItems = {
  eventId: string;
  advancedStatistics?: boolean;
  exportExhibitorsModule?: boolean;
  exportProgramModule?: boolean;
  promotionDates?: Date[] | null;
  exhibitorPromotionCredits?: number | null;
  exhibitorProfilesPack1Credits?: number | null;
  exhibitorProfilesPack2Credits?: number | null;
  exhibitorProfilesPack3Credits?: number | null;
  cashRegisterCredits?: number | null;
  aiImageCredits?: number | null;
  eventCustomPdfAppearance?: boolean;
};

type UpdateEventItemsPayload = Omit<UpdateEventItems, "eventId">;

const formatEventItemsPayload = (body: UpdateEventItemsPayload) => {
  if (!Object.prototype.hasOwnProperty.call(body, "promotionDates")) {
    return body;
  }

  const { promotionDates, ...rest } = body;

  return {
    ...rest,
    promotionDates: promotionDates?.map(toDatabaseFormat) ?? null,
  };
};

export async function updateEventItems({ eventId, ...body }: UpdateEventItems) {
  const { data } = await privateAxios.patch<void>(
    `/basket/items/events/${eventId}`,
    formatEventItemsPayload(body)
  );
  return data;
}

export type UpdateMultipleEventsItems = {
  events: Array<{
    eventId: string;
    advancedStatistics: boolean;
  }>;
};

export async function updateMultipleEventsItems(
  events: UpdateMultipleEventsItems
) {
  const { data } = await privateAxios.patch<void>(
    `/basket/items/events`,
    events
  );
  return data;
}

export type UpdatePrestationsPurchaseItems = {
  events: UpdateEventItems[];
};

export async function updatePrestationsPurchaseItems({
  events,
}: UpdatePrestationsPurchaseItems) {
  const { data } = await privateAxios.patch<void>(
    `/basket/items/events/prestations`,
    {
      events: events.map(({ eventId, ...body }) => ({
        eventId,
        ...formatEventItemsPayload(body),
      })),
    }
  );
  return data;
}

export type UpdateMultipleExhibitorAdvancedStatsItems = {
  events: Array<{
    eventId: string;
    structureId: string;
  }>;
};

export async function updateMultipleExhibitorAdvancedStatItems(
  events: UpdateMultipleExhibitorAdvancedStatsItems
) {
  const { data } = await privateAxios.patch<void>(
    `/basket/items/exhibitor-advanced-statistics`,
    events
  );
  return data;
}

export function removeItem(itemId: string) {
  return privateAxios.delete<void>(`/basket/items/${itemId}`);
}

type RemoveItemGroupArgs = {
  groupKey: string;
  type: BasketType;
};

export function removeItemGroup({ groupKey, type }: RemoveItemGroupArgs) {
  return privateAxios.delete<void>(`/basket/items/group/${groupKey}`, {
    params: {
      type,
    },
  });
}

export const useRemoveItemGroupMutation = (type: BasketType) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (groupKey: string) => removeItemGroup({ type, groupKey }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: basketQueryKeys.get(type),
      });
      queryClient.invalidateQueries({
        queryKey: basketQueryKeys.activityWarning(),
      });
    },
  });
};

export async function applyDiscountCode(body: ApplyDiscountCodeForm) {
  try {
    const res = await privateAxios.post<
      {
        replacedCodesMessage: string;
      }[]
    >("/basket/discount-code", body);
    return res.data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
}

export async function removeDiscountCode(data: {
  basketId: string;
  discountCodeId: string;
}) {
  try {
    const res = await privateAxios.delete("/basket/discount-code", { data });
    return res.data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
}

export const useApplyDiscountCodeMutation = (type: BasketType = "store") => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: applyDiscountCode,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: basketQueryKeys.get(type),
      });
      queryClient.invalidateQueries({
        queryKey: basketQueryKeys.activityWarning(),
      });
    },
  });
};

export const useRemoveDiscountCodeMutation = (type: BasketType = "store") => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: removeDiscountCode,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: basketQueryKeys.get(type),
      });
      queryClient.invalidateQueries({
        queryKey: basketQueryKeys.activityWarning(),
      });
    },
  });
};

export type UpdateEventEquiments = {
  eventEquipmentRequestId: string;
  isPaying: boolean;
};

export async function updateEventEquiments({
  eventEquipmentRequestId,
  ...body
}: UpdateEventEquiments) {
  const { data } = await privateAxios.patch<void>(
    `/basket/items/event-equipments/${eventEquipmentRequestId}`,
    body
  );
  return data;
}

export const useUpdateEventEquimentsMutation = () => {
  return useMutation({
    mutationFn: updateEventEquiments,
  });
};

export type UpdateBasketDeadlineForm = {
  date: Date;
};

export async function updateBasketDeadline(
  basketId: string,
  body: UpdateBasketDeadlineForm
) {
  const { data } = await privateAxios.patch<void>(
    `/basket/${basketId}/deadline`,
    body
  );
  return data;
}

export const useUpdateBasketDeadlineMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      basketId,
      body,
    }: {
      basketId: string;
      body: UpdateBasketDeadlineForm;
    }) => updateBasketDeadline(basketId, body),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: basketQueryKeys.all,
      });
      queryClient.invalidateQueries({
        queryKey: basketQueryKeys.getById(variables.basketId),
      });
    },
  });
};

export function extendBasketExpirationTime() {
  return privateAxios.post<GetUserBasket>("/basket/extend-expiration-time");
}

export const useExtendBasketExpirationTime = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: extendBasketExpirationTime,
    onSuccess: (_, __, context) => {
      queryClient.setQueryData<GetUserBasket>(
        basketQueryKeys.get("backoffice"),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            hasExtendedExpirationTime: true,
          };
        }
      );
    },
  });
};
